'use client';

import { useClientConfig } from '@/lib/clients/ClientContext';

/**
 * Home-screen promo banners — two (or more) rectangular slots rendered above
 * the "popular products" grid. Driven by `ui.promoBanners` config: a client
 * with no banners configured renders nothing. A slot with an `imageUrl` shows
 * the artwork (optionally linked); a slot without one shows a styled
 * placeholder so the banner area is visible before artwork is supplied.
 */
export default function PromoBanners() {
  const clientConfig = useClientConfig();
  const banners = clientConfig.ui.promoBanners;
  if (!banners || banners.length === 0) return null;

  const resolveSrc = (url: string) =>
    url.startsWith('/') ? `${clientConfig.apiOrigin ?? ''}${url}` : url;

  // Product rule: at most TWO slots. A single banner becomes a full-width
  // "hero" strip (double the width of a 2-up slot, same height → aspect 32/5)
  // so it covers the row instead of sitting half-empty. Two banners render as
  // the classic 2-up 16/5 pair. Any extra slots beyond two are ignored (the
  // dashboard also enforces the cap at write time).
  const visibleBanners = banners.slice(0, 2);
  const single = visibleBanners.length === 1;

  return (
    <div className="w-full px-6 pt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
      {visibleBanners.map((banner, i) => {
        const inner = banner.imageUrl ? (
          <img
            src={resolveSrc(banner.imageUrl)}
            alt={banner.alt || 'Promo'}
            className="w-full h-full object-cover"
          />
        ) : (
          // Placeholder — shown until the client supplies artwork.
          <div className="w-full h-full flex flex-col items-center justify-center gap-1 text-gray-400 bg-[#F3F4F6] border border-dashed border-[#D1D5DB]">
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="text-[12px] font-gotham">{banner.alt || 'Banner'}</span>
          </div>
        );

        // Constant artwork ratio per mode across breakpoints (object-cover
        // would crop otherwise): single → 32/5 spanning both columns.
        const frame = single
          ? 'block w-full sm:col-span-2 aspect-[32/5] overflow-hidden bg-white'
          : 'block w-full aspect-[16/5] overflow-hidden bg-white';
        return banner.linkUrl ? (
          <a key={i} href={banner.linkUrl} target="_blank" rel="noopener noreferrer" className={`${frame} cursor-pointer`}>
            {inner}
          </a>
        ) : (
          <div key={i} className={frame}>
            {inner}
          </div>
        );
      })}
    </div>
  );
}
